import React, { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'
import { translateNumberToBengali, translateGroupToBengali, getCollegeAcronym } from './TestimonialPage'

export default function VerifyTestimonialPage() {
  const [searchParams] = useSearchParams()
  const certId = searchParams.get('id')
  const [loading, setLoading] = useState(true)
  const [certData, setCertData] = useState<any>(null)
  const [collegeData, setCollegeData] = useState<any>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const verifyCertificate = async () => {
    if (!certId) {
      setErrorMsg('No Certificate ID provided in query parameters.')
      setLoading(false)
      return
    }

    try {
      // Fetch request and join with student database
      const { data, error } = await supabase
        .from('student_testimonial_requests')
        .select('*, student:database_fgc(*)')
        .eq('id', certId)
        .single()

      if (error || !data) {
        setErrorMsg('Invalid Testimonial Certificate ID. This certificate could not be verified.')
        setLoading(false)
        return
      }

      setCertData(data)

      // Fetch college profile info
      const { data: col, error: colErr } = await supabase
        .from('college_user')
        .select('*')
        .eq('college_eiin', data.college_eiin)
        .single()

      if (!colErr && col) {
        setCollegeData(col)
      }
    } catch (err: any) {
      setErrorMsg('An error occurred during verification.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    verifyCertificate()
  }, [certId])

  return (
    <div style={styles.page}>
      {/* Inject Kalpurush & Tiro Bangla Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&family=Tiro+Bangla&display=swap');
        .kalpurush-verify-page {
          font-family: 'Tiro Bangla', 'Hind Siliguri', 'Kalpurush', 'SolaimanLipi', 'Aneek Bangla', serif !important;
        }
      `}</style>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <EmsLogo size={32} />
          <span style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>
            EMS verification center
          </span>
        </div>
        <Link to="/" style={styles.btnBack}>Back to Portal</Link>
      </header>

      <main style={styles.main}>
        {loading ? (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <div style={styles.spinner}></div>
              <h3 style={{ marginTop: '16px', color: '#475569' }}>Verifying Certificate Authenticity...</h3>
            </div>
          </div>
        ) : errorMsg ? (
          <div style={{ ...styles.card, borderColor: '#fca5a5', background: '#fffbeb' }}>
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <span style={{ fontSize: '48px' }}>❌</span>
              <h2 style={{ color: '#b91c1c', fontWeight: 900, margin: '14px 0' }}>Verification Failed</h2>
              <p style={{ color: '#7f1d1d', fontWeight: 700, maxWidth: '450px', margin: '0 auto 20px', lineHeight: '1.6' }}>{errorMsg}</p>
              <Link to="/" style={styles.btnAction}>Go to Home</Link>
            </div>
          </div>
        ) : (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <span style={{ fontSize: '50px', display: 'block', marginBottom: '10px' }}>🛡️</span>
              <span style={styles.badgeSuccess}>✓ VERIFIED </span>
              <h2 style={{ margin: '14px 0 4px', fontWeight: 900, color: '#0f172a' }}>
                Testimonial  (প্রত্যয়ন পত্র)
              </h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 700 }}>
                  {collegeData?.college_name_en || collegeData?.college_name || 'Institution'}
              </p>
            </div>

            {/* Verification Details Table */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', background: '#f8fafc' }}>
              <div style={styles.tableRow}>
                <span style={styles.label}>Certificate Serial No</span>
                <strong style={{ ...styles.val, color: '#2563eb' }}>
                  {translateNumberToBengali(
                    certData.certificate_no?.replace(/^TEST\//i, `${getCollegeAcronym(collegeData?.college_name)}/`) || 
                    `${getCollegeAcronym(collegeData?.college_name)}/${collegeData?.college_eiin}/2026/${String(certData.id).padStart(4, '0')}`
                  )}
                </strong>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Issuing Institution</span>
                <span style={styles.val}>
                  <strong>{collegeData?.college_name_en || collegeData?.college_name || 'N/A'}</strong> ({collegeData?.college_name}) | EIIN: {translateNumberToBengali(collegeData?.college_eiin || 'N/A')}
                </span>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Student Name</span>
                <strong style={styles.val}>{certData.student?.student_name_bn || certData.student?.student_name_en || '—'}</strong>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Father Name</span>
                <span style={styles.val}>{certData.student?.father_name_bn || certData.student?.father_name_en || '—'}</span>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Mother Name</span>
                <span style={styles.val}>{certData.student?.mother_name_bn || certData.student?.mother_name_en || '—'}</span>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Class Roll & Group</span>
                <span style={styles.val}>Roll: {translateNumberToBengali(certData.student?.class_roll || '—')} | Group: {translateGroupToBengali(certData.student?.group)}</span>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Session & GPA</span>
                <span style={styles.val}>Session: {translateNumberToBengali(certData.student?.session || '—')} | GPA: {translateNumberToBengali(certData.gpa || '—')}</span>
              </div>
              <div style={styles.tableRow}>
                <span style={styles.label}>Approved At</span>
                <span style={styles.val}>
                  {new Date(certData.approved_at || Date.now()).toLocaleDateString('bn-BD')} ({new Date(certData.approved_at || Date.now()).toLocaleTimeString('bn-BD')})
                </span>
              </div>
              <div style={{ ...styles.tableRow, borderBottom: 'none' }}>
                <span style={styles.label}>Principal Approval Authority</span>
                <span style={styles.val}><strong>{collegeData?.principal_name_en || collegeData?.principal_name || 'Principal Office'}</strong> ({collegeData?.principal_name})</span>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button onClick={() => window.print()} style={styles.btnAction}>
                🖨️ Print Verification Record
              </button>
            </div>
          </div>
        )}
      </main>

      <footer style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', marginTop: '40px', paddingBottom: '30px' }}>
        &copy; {new Date().getFullYear()} EMS {collegeData?.college_name || 'College'} Authority. All rights reserved.
      </footer>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Tiro Bangla', 'Hind Siliguri', 'Kalpurush', 'SolaimanLipi', 'Aneek Bangla', serif",
    background: '#f1f5f9',
    minHeight: '100vh',
    color: '#0f172a'
  },
  header: {
    background: '#ffffff',
    padding: '14px 28px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  btnBack: {
    textDecoration: 'none',
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 14px',
    borderRadius: '10px',
    fontWeight: 700,
    fontSize: '13px'
  },
  main: {
    maxWidth: '600px',
    margin: '40px auto',
    padding: '0 20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '24px',
    padding: '30px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)'
  },
  badgeSuccess: {
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '999px',
    background: '#dcfce7',
    color: '#15803d',
    fontWeight: 900,
    fontSize: '13px',
    letterSpacing: '0.5px'
  },
  tableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 18px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '14px'
  },
  label: {
    color: '#64748b',
    fontWeight: 700
  },
  val: {
    textAlign: 'right',
    color: '#0f172a'
  },
  btnAction: {
    display: 'inline-block',
    textDecoration: 'none',
    padding: '10px 24px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#fff',
    fontWeight: 800,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255, 85, 0, 0.3)'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #ff5500',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto'
  }
}
